import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  createCalendarBlocker,
  updateCalendarBlocker,
  deleteCalendarBlocker,
  skipCalendarBlockerOccurrence,
} from '../services/api';
import { useDialog } from './DialogProvider';

const SHOW_AS_OPTIONS = [
  { value: 'Free', label: 'Free' },
  { value: 'Tentative', label: 'Tentative' },
  { value: 'Busy', label: 'Busy' },
  { value: 'OutOfOffice', label: 'Out of Office' },
];

const FREQ_OPTIONS = [
  { value: 'None', label: 'Does not repeat' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
];

const WEEKDAY_CODES = [
  { code: 'MO', label: 'M' },
  { code: 'TU', label: 'T' },
  { code: 'WE', label: 'W' },
  { code: 'TH', label: 'T' },
  { code: 'FR', label: 'F' },
  { code: 'SA', label: 'S' },
  { code: 'SU', label: 'S' },
];

// Convert UTC ISO to local "YYYY-MM-DDTHH:mm" for datetime-local inputs.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toLocalDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Local datetime-local string -> UTC ISO.
const fromLocalInput = (s) => {
  if (!s) return null;
  return new Date(s).toISOString();
};

const defaultStart = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
};

export default function BlockerDialog({ open, onClose, onSaved, mode, occurrence, blockerData }) {
  // mode: 'create' | 'edit-series' | 'view'
  const dialog = useDialog();
  const isEdit = mode === 'edit-series';
  const isView = mode === 'view';
  const isCreate = mode === 'create';

  const [title, setTitle] = useState('Busy');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [showAs, setShowAs] = useState('Busy');
  const [notes, setNotes] = useState('');
  const [freq, setFreq] = useState('None');
  const [interval, setIntervalVal] = useState(1);
  const [byDays, setByDays] = useState([]);
  const [endMode, setEndMode] = useState('never'); // never | until | count
  const [until, setUntil] = useState('');
  const [count, setCount] = useState(10);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (isCreate) {
      const s = defaultStart();
      const e = new Date(s.getTime() + 60 * 60 * 1000);
      setTitle('Busy');
      setStart(toLocalInput(s.toISOString()));
      setEnd(toLocalInput(e.toISOString()));
      setIsAllDay(false);
      setShowAs('Busy');
      setNotes('');
      setFreq('None');
      setIntervalVal(1);
      setByDays([]);
      setEndMode('never');
      setUntil('');
      setCount(10);
    } else if (blockerData) {
      setTitle(blockerData.title || '');
      setStart(toLocalInput(blockerData.startTime));
      setEnd(toLocalInput(blockerData.endTime));
      setIsAllDay(!!blockerData.isAllDay);
      setShowAs(blockerData.showAs || 'Busy');
      setNotes(blockerData.notes || '');
      setFreq(blockerData.recurrenceFrequency || 'None');
      setIntervalVal(blockerData.recurrenceInterval || 1);
      setByDays(blockerData.recurrenceByWeekDays
        ? blockerData.recurrenceByWeekDays.split(',').map(s => s.trim()) : []);
      if (blockerData.recurrenceUntil) {
        setEndMode('until');
        setUntil(toLocalDateInput(blockerData.recurrenceUntil));
      } else if (blockerData.recurrenceCount) {
        setEndMode('count');
        setCount(blockerData.recurrenceCount);
      } else {
        setEndMode('never');
      }
    }
  }, [open, isCreate, blockerData]);

  const buildPayload = () => {
    const startIso = isAllDay
      ? new Date(`${start.split('T')[0]}T00:00`).toISOString()
      : fromLocalInput(start);
    const endIso = isAllDay
      ? new Date(`${end.split('T')[0]}T00:00`).toISOString()
      : fromLocalInput(end);
    const payload = {
      title: title.trim() || 'Busy',
      startTime: startIso,
      endTime: endIso,
      isAllDay,
      showAs,
      notes: notes || null,
      recurrenceFrequency: freq,
      recurrenceInterval: parseInt(interval, 10) || 1,
      recurrenceByWeekDays: freq === 'Weekly' && byDays.length
        ? byDays.join(',') : null,
      recurrenceUntil: freq !== 'None' && endMode === 'until' && until
        ? new Date(`${until}T23:59`).toISOString() : null,
      recurrenceCount: freq !== 'None' && endMode === 'count'
        ? parseInt(count, 10) || null : null,
    };
    return payload;
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title required'); return; }
    if (!start || !end) { setError('Start and end required'); return; }
    if (new Date(end) <= new Date(start) && !isAllDay) {
      setError('End must be after start');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = buildPayload();
      if (isCreate) {
        await createCalendarBlocker(payload);
      } else {
        await updateCalendarBlocker(blockerData.id, payload);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDeleteSeries = async () => {
    if (!(await dialog.confirm({ title: 'Delete blocker', message: 'Delete this blocker (all occurrences)?', confirmText: 'Delete', danger: true }))) return;
    setSaving(true);
    try {
      await deleteCalendarBlocker(blockerData.id);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data || 'Delete failed');
    } finally { setSaving(false); }
  };

  const handleSkipOccurrence = async () => {
    if (!occurrence) return;
    if (!(await dialog.confirm({ title: 'Skip occurrence', message: 'Skip this occurrence only?', confirmText: 'Skip' }))) return;
    setSaving(true);
    try {
      await skipCalendarBlockerOccurrence(blockerData.id, occurrence.startTime);
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data || 'Skip failed');
    } finally { setSaving(false); }
  };

  const toggleDay = (code) => {
    setByDays(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const titleText = isCreate ? 'Block Time' : isEdit ? 'Edit Blocker' : 'Blocker';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{titleText}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            size="small"
            disabled={isView}
            fullWidth
          />
          <FormControlLabel
            control={<Checkbox checked={isAllDay} onChange={e => setIsAllDay(e.target.checked)} disabled={isView} />}
            label="All day"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Start"
              type={isAllDay ? 'date' : 'datetime-local'}
              value={isAllDay ? (start.split('T')[0] || '') : start}
              onChange={e => setStart(isAllDay ? `${e.target.value}T00:00` : e.target.value)}
              size="small"
              disabled={isView}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label="End"
              type={isAllDay ? 'date' : 'datetime-local'}
              value={isAllDay ? (end.split('T')[0] || '') : end}
              onChange={e => setEnd(isAllDay ? `${e.target.value}T00:00` : e.target.value)}
              size="small"
              disabled={isView}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Stack>

          <TextField
            label="Show as"
            select
            value={showAs}
            onChange={e => setShowAs(e.target.value)}
            size="small"
            disabled={isView}
            fullWidth
          >
            {SHOW_AS_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Repeats"
            select
            value={freq}
            onChange={e => setFreq(e.target.value)}
            size="small"
            disabled={isView}
            fullWidth
          >
            {FREQ_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          {freq !== 'None' && (
            <>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <Typography variant="body2">Every</Typography>
                <TextField
                  type="number"
                  value={interval}
                  onChange={e => setIntervalVal(e.target.value)}
                  size="small"
                  disabled={isView}
                  inputProps={{ min: 1, max: 365 }}
                  sx={{ width: 80 }}
                />
                <Typography variant="body2">
                  {freq === 'Daily' ? 'day(s)' : freq === 'Weekly' ? 'week(s)' : 'month(s)'}
                </Typography>
              </Stack>

              {freq === 'Weekly' && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Days of week (defaults to start day)
                  </Typography>
                  <ToggleButtonGroup size="small" disabled={isView}>
                    {WEEKDAY_CODES.map(d => (
                      <ToggleButton
                        key={d.code}
                        value={d.code}
                        selected={byDays.includes(d.code)}
                        onClick={() => toggleDay(d.code)}
                      >
                        {d.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              <TextField
                label="Ends"
                select
                value={endMode}
                onChange={e => setEndMode(e.target.value)}
                size="small"
                disabled={isView}
                fullWidth
              >
                <MenuItem value="never">Never</MenuItem>
                <MenuItem value="until">On date</MenuItem>
                <MenuItem value="count">After N occurrences</MenuItem>
              </TextField>
              {endMode === 'until' && (
                <TextField
                  label="End date"
                  type="date"
                  value={until}
                  onChange={e => setUntil(e.target.value)}
                  size="small"
                  disabled={isView}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              )}
              {endMode === 'count' && (
                <TextField
                  label="Occurrences"
                  type="number"
                  value={count}
                  onChange={e => setCount(e.target.value)}
                  size="small"
                  disabled={isView}
                  inputProps={{ min: 1, max: 999 }}
                  fullWidth
                />
              )}
            </>
          )}

          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            size="small"
            disabled={isView}
            multiline
            minRows={2}
            fullWidth
          />

          {error && <Typography variant="caption" color="error">{String(error)}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isEdit && occurrence?.isRecurring && (
          <Button size="small" color="warning" onClick={handleSkipOccurrence} disabled={saving}>
            Skip This Occurrence
          </Button>
        )}
        {isEdit && (
          <Button size="small" color="error" onClick={handleDeleteSeries} disabled={saving}>
            Delete {occurrence?.isRecurring ? 'Series' : ''}
          </Button>
        )}
        <Button size="small" onClick={onClose} disabled={saving}>Cancel</Button>
        {!isView && (
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
            {isCreate ? 'Block Time' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
